

import React from 'react';
import { FederatedModule } from '@bfme-technology/spa';
import { getResolvedMfeProps } from '../Dashboard.utils';



import type { InvestmentPortfolioProps } from './InvestmentPortfolio.types';

export const InvestmentPortfolio: React.FC<InvestmentPortfolioProps> = ({ routesLoading = false, routes = [] }) => {
  const route = routes.find((r) => r.key === 'mfe-investing');

  if (routesLoading) {
    return (
      <section className="glass-panel p-5 md:p-6 flex flex-col gap-4 items-center justify-center min-h-[150px]">
        <div className="text-text-muted text-xs">Loading Investment Portfolio MFE...</div>
      </section>
    );
  }

  if (!route) {
    return (
      <section className="glass-panel p-5 md:p-6 flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <h3 className="text-sm md:text-base font-bold text-text-primary flex items-center gap-2">
            <i className="fa-solid fa-chart-line text-primary-accent"></i>
            Investment Portfolio
          </h3>
          <span className="bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded-lg text-[10px] font-extrabold">+1.24% today</span>
        </div>

        <div className="flex flex-col gap-3.5">
          {/* Asset 1: VOO */}
          <div className="flex items-center justify-between p-3 rounded-2xl bg-bg-deep border border-border-color hover:border-primary-accent/20 transition-all duration-300">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-blue-500/10 text-primary-accent shrink-0 font-bold text-xs">
                VOO
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-xs md:text-sm font-bold text-text-primary">Vanguard S&P 500 ETF</span>
                <span className="text-[10px] text-text-muted">12 Shares • Allocation 30%</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <svg width="40" height="16" className="overflow-visible hidden sm:block">
                <path d="M0,10 Q10,14 20,4 T40,2" fill="none" stroke="var(--primary-accent)" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <div className="text-right shrink-0 flex flex-col items-end gap-1">
                <span className="text-xs md:text-sm font-extrabold text-text-primary font-display">$6,144.00</span>
                <span className="px-1.5 py-0.5 rounded text-[8px] font-extrabold uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">+0.42%</span>
              </div>
            </div>
          </div>

          {/* Asset 2: AAPL */}
          <div className="flex items-center justify-between p-3 rounded-2xl bg-bg-deep border border-border-color hover:border-primary-accent/20 transition-all duration-300">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-purple-500/10 text-accent-violet shrink-0 font-bold text-xs">
                AAPL
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-xs md:text-sm font-bold text-text-primary">Apple Inc.</span>
                <span className="text-[10px] text-text-muted">24 Shares • Allocation 22%</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <svg width="40" height="16" className="overflow-visible hidden sm:block">
                <path d="M0,12 Q10,4 20,8 T40,0" fill="none" stroke="var(--accent-violet)" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <div className="text-right shrink-0 flex flex-col items-end gap-1">
                <span className="text-xs md:text-sm font-extrabold text-text-primary font-display">$4,582.40</span>
                <span className="px-1.5 py-0.5 rounded text-[8px] font-extrabold uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">+1.85%</span>
              </div>
            </div>
          </div>

          {/* Asset 3: ETH */}
          <div className="flex items-center justify-between p-3 rounded-2xl bg-bg-deep border border-border-color hover:border-primary-accent/20 transition-all duration-300">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-pink-500/10 text-accent-magenta shrink-0 font-bold text-xs">
                ETH
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-xs md:text-sm font-bold text-text-primary">Ethereum</span>
                <span className="text-[10px] text-text-muted">1.5 ETH • Allocation 26%</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <svg width="40" height="16" className="overflow-visible hidden sm:block">
                <path d="M0,2 Q10,12 20,4 T40,14" fill="none" stroke="var(--accent-magenta)" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <div className="text-right shrink-0 flex flex-col items-end gap-1">
                <span className="text-xs md:text-sm font-extrabold text-text-primary font-display">$5,250.00</span>
                <span className="px-1.5 py-0.5 rounded text-[8px] font-extrabold uppercase bg-red-500/10 text-rose-400 border border-red-500/20">-1.20%</span>
              </div>
            </div>
          </div>

          {/* Asset 4: TSLA */}
          <div className="flex items-center justify-between p-3 rounded-2xl bg-bg-deep border border-border-color hover:border-primary-accent/20 transition-all duration-300">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-amber-500/10 text-amber-500 shrink-0 font-bold text-xs">
                TSLA
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-xs md:text-sm font-bold text-text-primary">Tesla Inc.</span>
                <span className="text-[10px] text-text-muted">15 Shares • Allocation 17%</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <svg width="40" height="16" className="overflow-visible hidden sm:block">
                <path d="M0,14 Q10,16 20,6 T40,2" fill="none" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <div className="text-right shrink-0 flex flex-col items-end gap-1">
                <span className="text-xs md:text-sm font-extrabold text-text-primary font-display">$3,420.00</span>
                <span className="px-1.5 py-0.5 rounded text-[8px] font-extrabold uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">+3.12%</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const resolved: any = getResolvedMfeProps(route);
  const { key, ...mfeProps } = resolved;
  return (
    <FederatedModule
      {...mfeProps}
      mfeType="federated"
      scope="mfe_investing"
      module="./InvestmentPortfolio"
    />
  );
};
